import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from '@angular/material/dialog';
import {TranslateService, TranslateModule} from '@ngx-translate/core';
import {NgIf, NgStyle} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {DialogWidth} from '@q9elements/ui-kit/common';
import {
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent
} from '@q9elements/ui-kit/components';

@Component({
  selector: 'app-confirm-delete-dialog',
  templateUrl: './confirm-delete-dialog.component.html',
  styleUrls: ['./confirm-delete-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    NgIf,
    TranslateModule,
    DialogCloseButtonComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    InfoMessageComponent,
    NgStyle
  ]
})
export class ConfirmDeleteDialogComponent {
  public message: string;
  public title: string;
  public warningMessage = '';
  public additionalMessage = '';
  public confirmButtonText: string;
  public dialogWidth = DialogWidth.SM;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: Array<DialogType>,
    private translateSvc: TranslateService,
    private dialogRef: MatDialogRef<ConfirmDeleteDialogComponent>
  ) {
    let type = null;

    if (data.length > 1) {
      type = DialogType.DEL_GROUP;
    } else {
      type = data.pop();
    }

    switch (type) {
      case DialogType.DEL_DT:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_DT_DEL');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('POPUPS.DELETE_ATTACHMENT');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;

      case DialogType.DEL_NOTE:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_NOTE_DEL');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('POPUPS.DELETE_ATTACHMENT');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;

      case DialogType.DEL_DD:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_DD_DEL');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('EDITOR.DELETE');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;

      case DialogType.DEL_CONN:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_CONN_DEL');
        this.title = this.translateSvc.instant('EDITOR.DELETE');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;

      case DialogType.DEL_GROUP: {
        const groupMessage = this.buildGroupMessage(data);

        this.message = groupMessage.start;
        this.additionalMessage = groupMessage.toDelete;
        this.warningMessage = groupMessage.warning;
        this.title = this.translateSvc.instant('POPUPS.GROUP_DELETE_TITLE');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;
      }

      case DialogType.SEND_TO_CHILD:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_SEND_CHILD');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('EDITOR.SEND_TO_CHILD');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_SEND_CHILD_BTN');
        break;

      case DialogType.DEL_FL_CONN:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_FL_CONN_DEL');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('EDITOR.DELETE');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;

      case DialogType.DEL_URL: {
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_URL_DEL');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('POPUPS.DELETE_ATTACHMENT');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;
      }

      case DialogType.DEL_IMAGE:
        this.message = this.translateSvc.instant('POPUPS.CONFIRM_IMAGE_DEL');
        this.warningMessage = this.translateSvc.instant('POPUPS.NOT_UNDOABLE');
        this.title = this.translateSvc.instant('POPUPS.DELETE_ATTACHMENT');
        this.confirmButtonText = this.translateSvc.instant('POPUPS.CONFIRM_DEL_SUBMIT');
        break;
    }
  }

  private buildGroupMessage(flags: Array<number>) {
    const msgStart = this.translateSvc.instant('POPUPS.CONFIRM_DEL_MSG');
    const msgEnd = this.translateSvc.instant('POPUPS.CONFIRM_SUBMSG');
    const toDelete = flags.map(type => {
      switch (type) {
        case DialogType.DEL_DT:
          return this.translateSvc.instant('ELEMENTS.DATATABLE');

        case DialogType.DEL_DD:
          return this.translateSvc.instant('ELEMENTS.DRILLDOWN');

        case DialogType.DEL_CONN:
          return this.translateSvc.instant('ELEMENTS.CONNECTOR');

        case DialogType.DEL_URL:
          return this.translateSvc.instant('ELEMENTS.URL');

        case DialogType.DEL_IMAGE:
          return this.translateSvc.instant('ELEMENTS.IMAGE');

        case DialogType.DEL_NOTE:
          return this.translateSvc.instant('ELEMENTS.NOTE');
      }
    });

    return {
      start: msgStart,
      toDelete: toDelete.join(', '),
      warning: msgEnd
    };
  }

  public confirm() {
    this.dialogRef.close(true);
  }

  public cancel() {
    this.dialogRef.close(false);
  }
}

export enum DialogType {
  DEL_DT,
  DEL_DD,
  DEL_CONN,
  DEL_GROUP,
  SEND_TO_CHILD,
  DEL_FL_CONN,
  DEL_NOTE,
  DEL_URL,
  DEL_IMAGE
}
